import { zodResolver } from "@hookform/resolvers/zod";
import type { AlertColor } from "@mui/material/Alert/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { AxiosError } from "axios";
import { useForm, useWatch } from "react-hook-form";

import useQueryClient from "../../../api/query";
import { stall } from "../../../utils/utils";
import { CREDENTIAL_TYPE } from "../common";
import type { CredentialsStore } from "../store";
import { AddPayerCredentialFormFields } from "./AddPayerCredentialFormFields";
import {
  type AddPayerCredentialsFormFields,
  addPayerCredentialsFormSchemaBase,
} from "./PayerCredentialsForm.zod";

interface AddPayerCredentialsProps {
  sources: SourceMessage[];
  pmses: PMSMessage[];
  practices: PracticeMessage[];
  userCredentials: CredentialsStore["userCredentials"];
  cancel: () => void;
  initialValues?: Partial<AddPayerCredentialsFormFields>;
  afterFormSubmit?: ({
    sourceId,
    practiceId,
    username,
    password,
  }: Partial<UserCredentialsMessage>) => Promise<void>;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
}

export function AddPayerCredentials({
  sources,
  pmses,
  practices,
  userCredentials,
  cancel,
  initialValues = undefined,
  afterFormSubmit,
  updateSnackBar,
}: AddPayerCredentialsProps) {
  const defaultPracticeId = practices.length === 1 ? practices[0].wieldyId : "";

  const {
    control,
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    setValue,
  } = useForm<
    AddPayerCredentialsFormFields & {
      activationCode?: string;
      serialNumber?: string;
      customerNumber?: string;
      machineIdentifier?: string;
    }
  >({
    mode: "onChange",
    reValidateMode: "onChange",
    delayError: 1000,
    resolver: zodResolver(addPayerCredentialsFormSchemaBase),
    defaultValues: {
      practiceId: defaultPracticeId,
      ...initialValues,
    },
  });

  const selectedPractice = useWatch({
    control,
    name: "practiceId",
  });

  const selectedType = useWatch({
    control,
    name: "type",
  });

  const selectedSource = useWatch({
    control,
    name: "source",
  });

  const selectedPMS = useWatch({
    control,
    name: "pms",
  });

  const queryClient = useQueryClient();

  const onSubmit = async ({
    type,
    source,
    pms,
    unsupportedPayer,
    practiceId,
    username,
    password,
    website,
    notes,
    mfaJson,
    userType,
    userId,
    activationCode,
    serialNumber,
    customerNumber,
    machineIdentifier,
  }: AddPayerCredentialsFormFields) => {
    // Check for duplicate before hitting the API
    const duplicate = (userCredentials ?? []).find((c) => {
      if (c.practiceId !== practiceId) return false;
      if (c.userType !== userType || c.userId !== userId) return false;
      if (type === CREDENTIAL_TYPE.PAYER) return c.sourceId === source;
      if (type === CREDENTIAL_TYPE.PMS) {
        const selectedPMSObj = pmses.find((p) => p.name === pms);
        return (
          !!selectedPMSObj && c.pmsDisplayName === selectedPMSObj.displayLabel
        );
      }
      return false;
    });

    if (duplicate) {
      updateSnackBar({
        severity: "warning",
        message:
          `A credential for this ${type === CREDENTIAL_TYPE.PMS ? "PMS" : "payer"} ` +
          `already exists for agent ${userType}: ${userId}. ` +
          `Please update the existing credential instead.`,
      });
      return;
    }

    try {
      if (type === CREDENTIAL_TYPE.PAYER) {
        if (source && !unsupportedPayer) {
          const body = {
            type,
            practiceId,
            sourceId: source,
            username,
            password,
            website,
            notes,
            mfaJson,
            userType,
            userId,
          };
          await Promise.all([
            await queryClient.postCreateCredentials(body),
            await stall(),
          ]);
        } else if (unsupportedPayer) {
          const body = {
            type: CREDENTIAL_TYPE.PAYER,
            practiceId,
            name: unsupportedPayer,
            username,
            password,
            website,
            notes,
            mfaJson,
            userType,
            userId,
          };
          await Promise.all([
            await queryClient.postOtherCredentials(body),
            await stall(),
          ]);
        }
      }

      if (type === CREDENTIAL_TYPE.PMS) {
        const selectedPMSObj = pmses.find((p) => p.name === pms);
        if (!selectedPMSObj || !website) {
          throw new Error("PMS and website are required.");
        }
        const body = {
          type,
          pmsId: selectedPMSObj.id,
          practiceId,
          username,
          password,
          website,
          userType,
          userId,
          activationCode,
          serialNumber,
          customerNumber,
          machineIdentifier,
        };
        await Promise.all([
          await queryClient.postCreatePMSCredentials(body),
          await stall(),
        ]);
      }

      if (afterFormSubmit) {
        await afterFormSubmit({
          practiceId,
          sourceId: source,
          username,
          password,
        });
      }
      updateSnackBar({
        severity: "success",
        message: "Successfully saved credentials.",
      });
    } catch (error: unknown) {
      const detail =
        error instanceof AxiosError ? error.response?.data?.detail : undefined;
      updateSnackBar({
        severity: "error",
        message:
          typeof detail === "string"
            ? detail
            : "Failed to save credentials. Try again.",
      });
      // biome-ignore lint/suspicious/noConsole: intentional browser console output
      console.error(`Failed to fetch credentials: ${error}`);
    }
  };

  const hasErrors = Object.keys(errors).length > 0;
  const isDisabled = isSubmitting || hasErrors || !isValid;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <AddPayerCredentialFormFields
        control={control}
        setValue={setValue}
        sources={sources}
        pmses={pmses}
        practices={practices}
        userCredentials={userCredentials}
        selectedPractice={selectedPractice}
        selectedType={selectedType}
        selectedSource={selectedSource}
        selectedPMS={selectedPMS}
        existingMfaJson={initialValues?.mfaJson}
        initialUserType={initialValues?.userType}
        initialUserId={initialValues?.userId}
        cancel={cancel}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          marginLeft: "auto",
          gap: "12px",
        }}
      >
        <Button
          sx={{
            backgroundColor: "#EAECF0",
            color: "#475467",
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "#e6e7ea",
            },
          }}
          onClick={() => cancel()}
          type="button"
          variant="contained"
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          sx={{ borderRadius: "8px" }}
          type="submit"
          color="primary"
          variant="contained"
          disabled={isDisabled}
          data-testid="submitCredentials"
        >
          {isSubmitting ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <CircularProgress size={20} />
              Submitting...
            </Box>
          ) : (
            "Continue"
          )}
        </Button>
      </Box>
    </Box>
  );
}

AddPayerCredentials.defaultProps = {
  afterFormSubmit: undefined,
};
